import { ref, StoreWithProps, useClassState } from "@web-monorepo/hooks";
import type { TranslatedString } from "@web-monorepo/translation-runtime";
import { useEffect } from "react";
import errorMessages, { ErrorType } from "src/pods/noise/data/errorMessages";
import logEvent from "src/utils/logEvent";

export const MAX_BAR_HEIGHT = 448;
export const MIN_BAR_HEIGHT = 10;
/** Web Audio API byte-frequency bins range 0–255; divisor for normalization. */
export const FREQUENCY_BIN_MAX = 256;
const FREQUENCY_SAMPLE_INTERVAL_MS = 60;

export type UseNoiseDisplayProps = {
  /** Sensitivity gain offset applied to each frequency bin (range roughly -125 to +125). */
  gain?: number;
  /** Called once when permission resolves or fails; receives the error name (or null on success). */
  onPermissionError?: (errorName: string | null) => void;
};

export type UseNoiseDisplayResult = {
  hasPermission: boolean | null;
  frequencies: number[] | null;
  permissionError: ErrorType | null;
  permissionErrorMessage: TranslatedString | "";
};

/**
 * Valtio-backed store for NoiseDisplay's mic permission + audio polling state.
 * Lives behind `useClassState` so the consuming hook never calls setState
 * directly — async results mutate the proxy fields here, and `useSnapshot()`
 * inside useClassState publishes those changes to the component.
 */
class NoiseDisplayStore extends StoreWithProps<UseNoiseDisplayProps> {
  hasPermission: boolean | null = null;
  frequencies: number[] | null = null;
  permissionError: ErrorType | null = null;

  // Non-reactive container for the audio chain teardown handle. ref() opts
  // it out of Valtio's deep-proxy so mutations don't churn snapshots or
  // trigger renders.
  private audioCtx = ref({ destroy: null as VoidFunction | null });

  get permissionErrorMessage(): TranslatedString | "" {
    return this.permissionError === null ? "" : ((errorMessages(this.permissionError) ?? "") as TranslatedString | "");
  }

  /**
   * Kick off `getUserMedia` and (on success) start the 60ms frequency
   * polling. Called once from the consuming hook's mount effect.
   */
  async start() {
    logEvent({ eventName: "noise.result.open" });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.handleStreamReady(stream);
    } catch (err) {
      if (!(err instanceof Error)) throw err;
      this.handleStreamError(err);
    }
  }

  /** Cleanup — torn down by the consuming hook's effect cleanup on unmount. */
  stop() {
    if (this.audioCtx.destroy) {
      this.audioCtx.destroy();
      this.audioCtx.destroy = null;
    }
    logEvent({ eventName: "noise.result.close" });
  }

  private handleStreamReady(stream: MediaStream) {
    this.hasPermission = true;
    this.permissionError = null;
    this.props.onPermissionError?.(null);

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 64;
    analyser.smoothingTimeConstant = 0.3;
    const audioBins = new Uint8Array(analyser.frequencyBinCount);
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const tick = () => {
      const currentGain = 1 + (this.props.gain || 0) / 255;
      analyser.getByteFrequencyData(audioBins);
      this.frequencies = Array.from(audioBins).map((bin) => Math.min(Math.max(Math.round(bin * currentGain), 0), 255));
    };

    const timer = setInterval(tick, FREQUENCY_SAMPLE_INTERVAL_MS);

    // Teardown can be triggered from several independent paths: the hook's
    // effect cleanup via stop(), and each audio track's "ended" event. Guard
    // synchronously so only the first one runs the chain down — AudioContext
    // .close() rejects with InvalidStateError once the context is closed.
    let destroyed = false;

    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      stream.getAudioTracks().forEach((track) => {
        track.stop();
        track.removeEventListener("ended", destroy);
      });
      source.disconnect(0);
      clearInterval(timer);
      if (audioContext.state !== "closed") {
        // Defense in depth: another code path could still close the context
        // between the state check and this call. Only the resulting
        // InvalidStateError is expected; anything else should still surface.
        void audioContext.close().catch((err: unknown) => {
          if (err instanceof Error && err.name === "InvalidStateError") return;
          throw err;
        });
      }
      this.frequencies = null;
    };
    stream.getAudioTracks().forEach((track) => track.addEventListener("ended", destroy, false));
    this.audioCtx.destroy = destroy;
  }

  private handleStreamError(err: Error) {
    let errorName = err.name;
    if (!errorName || errorName === "Error") {
      errorName = /not implemented/.test(err.message) ? "NotSupportedError" : "UnknownError";
    }
    this.hasPermission = false;
    this.permissionError = errorName as ErrorType;
    this.props.onPermissionError?.(errorName);
    logEvent({ eventName: "noise.unsupported" });
  }
}

export function useNoiseDisplay(props: UseNoiseDisplayProps): UseNoiseDisplayResult {
  const store = useClassState(NoiseDisplayStore, props);

  // `useClassState` returns a fresh Proxy wrapper each render, but the bound
  // methods inside it (`start` / `stop`) are stable references on the
  // underlying Valtio proxy. Depending on those directly avoids re-running
  // the effect (which would tear down and re-init the microphone on every
  // parent render, causing visible flicker).
  const { start, stop } = store;
  useEffect(() => {
    start();
    return () => stop();
  }, [start, stop]);

  return {
    hasPermission: store.hasPermission,
    frequencies: store.frequencies,
    permissionError: store.permissionError,
    permissionErrorMessage: store.permissionErrorMessage,
  };
}
